// Look up phonebook entries (internal, external, personal) by phone number.

export const digitsOf = (value) => String(value || "").replace(/\D/g, "");

// Typed text that is a phone number, possibly formatted: "+1 (561) 877-5080",
// "561 877 5080", "*72", "#31#". Letters make it a contact name instead.
const PHONE_LIKE = /^[\s()+.-]*[\d*#][\d\s()+.*#-]*$/;
export const isPhoneLike = (text) => PHONE_LIKE.test(String(text || ""));

/**
 * Shows a North American number as "+1 (561) 877-5080".
 * Ten digits, or eleven starting with 1, are formatted. Anything else is
 * returned unchanged: short numbers such as extensions, numbers of other
 * countries, and values that are not plain phone numbers (feature codes).
 */
export const formatUsPhone = (value) => {
  if (value === null || value === undefined) return value;
  const text = String(value).trim();
  if (text === "" || /[a-z*#]/i.test(text)) return value;
  const digits = text.replace(/\D/g, "");
  const national = digits.length === 10 ? digits
    : digits.length === 11 && digits[0] === "1" ? digits.slice(1)
    : null;
  if (!national) return value;
  return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
};

// What actually gets dialled: digits plus the * and # keypad characters.
export const dialDigits = (text) => String(text || "").replace(/[^\d*#]/g, "");

// Every number a phonebook entry can carry, across the three phonebook shapes.
export const contactNumbers = (contact) => {
  if (!contact) return [];
  const candidates = [
    contact.number,
    contact.phone,
    contact.voicemail && contact.voicemail.mailbox,
    contact.conversation && contact.conversation.number,
    contact.incomingProviderNumber && contact.incomingProviderNumber.number,
    contact.speedDial && contact.speedDial.number,
    contact.preferredDeviceNumbers && contact.preferredDeviceNumbers.office,
    contact.preferredDeviceNumbers && contact.preferredDeviceNumbers.mobile,
    contact.preferredDeviceNumbers && contact.preferredDeviceNumbers.custom,
    contact.numbers && contact.numbers.sms,
    contact.numbers && contact.numbers.office,
    contact.numbers && contact.numbers.mobile,
    contact.numbers && contact.numbers.custom,
  ];
  return candidates.map(digitsOf).filter(Boolean);
};

const numbersMatch = (a, b) => {
  if (a === b) return true;
  // Tolerate a missing country code / trunk prefix on either side.
  const shortest = Math.min(a.length, b.length);
  return shortest >= 7 && (a.endsWith(b) || b.endsWith(a));
};

export const contactDisplayName = (contact) =>
  contact ? `${contact.firstName || ""} ${contact.lastName || ""}`.trim() : "";

/**
 * Finds the first contact whose numbers match `number`, searching the
 * phonebooks in the given order (personal, internal, external by default).
 */
export const findContactByNumber = (phonebooks, number) => {
  const target = digitsOf(number);
  if (!target) return null;
  for (const book of phonebooks) {
    const found = (book || []).find((c) => contactNumbers(c).some((n) => numbersMatch(n, target)));
    if (found) return found;
  }
  return null;
};

// A real name contains letters; backends sometimes put the number itself in "name".
export const isRealName = (name) => /[a-z]/i.test(String(name || ""));

// Phonebook entry for a conversation, matched on its number (or pretty number).
export const findConversationContact = (conversation, phonebooks) =>
  findContactByNumber(phonebooks, conversation.number || conversation.number_pretty);

// Name to show for a conversation: its own (real) name, else a matching contact's.
export const conversationDisplayName = (conversation, phonebooks) => {
  if (isRealName(conversation.name)) return String(conversation.name).trim();
  const contact = findConversationContact(conversation, phonebooks);
  return contact ? contactDisplayName(contact) : "";
};
